import { Blockquote, Heading, Link } from '@radix-ui/themes';
import { BadgeUserRole } from 'components/common/badges/user-role';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { ActionSelect } from 'components/layout/action-select';
import { EB } from 'components/layout/error-boundary';
import { UserForm } from 'components/main/routes/users/form';
import { CommonTable } from 'components/table';
import { AuthContext } from 'contexts/auth';
import { DialogType } from 'enums/dialog';
import { RX } from 'enums/radix';
import { DialogState } from 'interfaces/dialog/state';
import { IssuerDoc } from 'lib/interfaces/database/issuer';
import { UserWhole } from 'lib/interfaces/database/user';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useState } from 'react';
import useDealersStore from 'stores/dealers';
import useUsersStore from 'stores/users';

enum ExtraAction {
  CreateDealer = 'CreateDealer',
}

export const Users = () => {
  const { data, create: createUser, load: loadUsers, update } = useUsersStore();
  const { create: createDealer } = useDealersStore();

  const { perms } = useContext(AuthContext);
  const { users: crudUsers, dealers: crudDealers } = perms;

  useEffect(() => {
    if (crudUsers?.read) {
      loadUsers();
    }
  }, [crudUsers?.read, loadUsers]);

  const [dialog, setDialog] = useState<DialogState>();

  // for confirming whether to make a user into a dealer
  const [confirm, setConfirm] = useState<{
    user: UserWhole;
    key: number;
  }>();

  return (
    <EB>
      <Heading>Users</Heading>
      <CommonTable
        defaultSort={{
          iCol: 1,
          asc: false,
        }}
        toolbarActions={[
          {
            hidden: !crudUsers?.create,
            label: 'Create',
            onClick: () =>
              setDialog({
                key: Date.now(),
                type: DialogType.Create,
                data: {},
              }),
          },
        ]}
        columns={[
          {
            header: 'Actions',
            rowFn: (item: UserWhole) => (
              <ActionSelect
                options={[
                  {
                    hidden: !crudUsers?.update,
                    label: 'Update',
                    value: DialogType.Update,
                  },
                  {
                    // only allow if user is not already a dealer; only allow w/ permissions to create dealers
                    hidden: item.isDealer || !crudDealers?.create,
                    label: 'Convert to Dealer',
                    value: ExtraAction.CreateDealer,
                  },
                ]}
                onValueChange={(v) => {
                  // new value never gets set but it does trigger the effect below
                  switch (v) {
                    case DialogType.Update: {
                      setDialog({
                        key: Date.now(),
                        type: v,
                        data: item,
                      });
                      break;
                    }

                    case ExtraAction.CreateDealer: {
                      setConfirm({
                        user: item,
                        key: Date.now(),
                      });
                      break;
                    }

                    default: {
                      break;
                    }
                  }
                }}
              />
            ),
          },
          {
            header: 'Created',
            rowFn: (item: UserWhole) =>
              StringUtil.printDateTime(item.createdAt),
            sortFn: (a: UserWhole, b: UserWhole) =>
              SortUtil.dates(a.createdAt, b.createdAt),
          },
          {
            header: 'Name',
            rowFn: (item: UserWhole) => item.fullName,
            sortFn: (a: UserWhole, b: UserWhole) =>
              SortUtil.strings(a.fullName, b.fullName),
          },
          {
            header: 'Email',
            rowFn: (item: UserWhole) => (
              <Link href={`mailto:${item.email}`}>{item.email}</Link>
            ),
            sortFn: (a: UserWhole, b: UserWhole) =>
              SortUtil.strings(a.email, b.email),
          },
          {
            header: 'Role',
            rowFn: (item: UserWhole) => <BadgeUserRole value={item.role} />,
            sortFn: (a: UserWhole, b: UserWhole) =>
              SortUtil.strings(a.role, b.role),
          },
        ]}
        data={data}
      />

      {dialog?.type === DialogType.Create && (
        <CommonFormDialog
          key={dialog.key}
          title="Create"
          children={<UserForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => createUser(item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Update && (
        <CommonFormDialog
          key={dialog.key}
          title="Update"
          children={<UserForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => update((dialog.data as IssuerDoc).id, item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {confirm && (
        <CommonFormDialog
          key={confirm.key}
          title="Update"
          children={
            <>
              <p>
                Are you sure you want to convert{' '}
                <Link href={`mailto:${confirm.user.email}`}>
                  {confirm.user.fullName}
                </Link>{' '}
                into a dealer?
              </p>

              <Blockquote color={RX.CLR.DANGER}>
                This permission will grant them access to view and manage
                issuers, securities, investors, etc... and should not be granted
                to anyone outside of the company.
              </Blockquote>
            </>
          }
          formData={undefined}
          submitAction={{
            label: 'Confirm',
            onClick: async () => {
              // create a new dealer
              const dealer = await createDealer(confirm.user);

              if (!dealer) {
                return false;
              }

              // reload users store
              loadUsers(true);

              return true;
            },
          }}
          onClose={() => setConfirm(undefined)}
        />
      )}
    </EB>
  );
};
